import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DateInputValidationError } from '@cbhq/cds-common/dates/DateInputValidationError';
import { useMultiSelect } from '@cbhq/cds-common/select/useMultiSelect';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import type { SelectDropdownComponent, SelectDropdownProps } from '@cbhq/cds-web/alpha/select';
import { DefaultSelectDropdown, Select } from '@cbhq/cds-web/alpha/select';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { DatePicker } from '@cbhq/cds-web/dates/DatePicker';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { getExportEndTime, getExportStartTime } from '../utils/helpers';

const messages = defineMessages({
  title: {
    id: 'Activity.auditExportTray.title',
    defaultMessage: 'Download activity',
    description: 'Title for the audit export tray',
  },
  closeAccessibilityLabel: {
    id: 'Activity.auditExportTray.closeAccessibilityLabel',
    defaultMessage: 'Close download activity tray',
    description: 'Accessibility label for the audit export tray close button',
  },
  actionTypeLabel: {
    id: 'Activity.auditExportTray.actionTypeLabel',
    defaultMessage: 'Action type',
    description: 'Label for the action type select field',
  },
  actionTypePlaceholder: {
    id: 'Activity.auditExportTray.actionTypePlaceholder',
    defaultMessage: 'All action types',
    description: 'Placeholder for the action type select field',
  },
  actorLabel: {
    id: 'Activity.auditExportTray.actorLabel',
    defaultMessage: 'Actor',
    description: 'Label for the actor select field',
  },
  actorPlaceholder: {
    id: 'Activity.auditExportTray.actorPlaceholder',
    defaultMessage: 'All actors',
    description: 'Placeholder for the actor select field',
  },
  startDateLabel: {
    id: 'Activity.auditExportTray.startDateLabel',
    defaultMessage: 'Start date',
    description: 'Label for the start date picker',
  },
  endDateLabel: {
    id: 'Activity.auditExportTray.endDateLabel',
    defaultMessage: 'End date',
    description: 'Label for the end date picker',
  },
  dateHelperText: {
    id: 'Activity.auditExportTray.dateHelperText',
    defaultMessage: 'mm/dd/yyyy',
    description: 'Helper text for the date picker fields',
  },
  invalidDateError: {
    id: 'Activity.auditExportTray.invalidDateError',
    defaultMessage: 'Please enter a valid date',
    description: 'Error message for invalid date input',
  },
  openStartDateCalendar: {
    id: 'Activity.auditExportTray.openStartDateCalendar',
    defaultMessage: 'Open start date calendar',
    description: 'Accessibility label for opening the start date calendar',
  },
  openEndDateCalendar: {
    id: 'Activity.auditExportTray.openEndDateCalendar',
    defaultMessage: 'Open end date calendar',
    description: 'Accessibility label for opening the end date calendar',
  },
  closeCalendar: {
    id: 'Activity.auditExportTray.closeCalendar',
    defaultMessage: 'Close calendar',
    description: 'Accessibility label for closing the date calendar',
  },
  nextMonth: {
    id: 'Activity.auditExportTray.nextMonth',
    defaultMessage: 'Next month',
    description: 'Accessibility label for the next month button in the calendar',
  },
  previousMonth: {
    id: 'Activity.auditExportTray.previousMonth',
    defaultMessage: 'Previous month',
    description: 'Accessibility label for the previous month button in the calendar',
  },
  actionTypeSelectAll: {
    id: 'Activity.auditExportTray.actionTypeSelectAll',
    defaultMessage: 'Select all',
    description: 'Label for the select all option in the action type multi-select',
  },
  actionTypeClearAll: {
    id: 'Activity.auditExportTray.actionTypeClearAll',
    defaultMessage: 'Clear all',
    description: 'Label for the clear all option in the action type multi-select',
  },
  actorSelectAll: {
    id: 'Activity.auditExportTray.actorSelectAll',
    defaultMessage: 'Select all',
    description: 'Label for the select all option in the actor multi-select',
  },
  actorClearAll: {
    id: 'Activity.auditExportTray.actorClearAll',
    defaultMessage: 'Clear all',
    description: 'Label for the clear all option in the actor multi-select',
  },
  cancelButton: {
    id: 'Activity.auditExportTray.cancelButton',
    defaultMessage: 'Cancel',
    description: 'Label for the cancel button in the audit export tray',
  },
  exportButton: {
    id: 'Activity.auditExportTray.exportButton',
    defaultMessage: 'Export',
    description: 'Label for the export button in the audit export tray',
  },
});

export type AuditExportSelectOption = {
  value: string;
  label: string;
};

export type AuditExportParams = {
  actionTypes: string[];
  actors: string[];
  startTime: string;
  endTime: string;
};

// Values used to pre-populate the tray (e.g. the "This view" preset)
export type AuditExportInitialValues = {
  actors?: string[];
  actionTypes?: string[];
  startTime?: string;
  endTime?: string;
};

type AuditExportTrayProps = {
  onClose: () => void;
  onExport: (params: AuditExportParams) => boolean | Promise<boolean>;
  isExporting?: boolean;
  actionTypeOptions: AuditExportSelectOption[];
  actorOptions: AuditExportSelectOption[];
  initialValues?: AuditExportInitialValues;
};

const TRAY_STYLES = {
  container: { width: '532px' },
  content: { overflow: 'visible' },
} as const;
const SELECT_STYLES = {
  root: { position: 'relative' },
  dropdown: { zIndex: zIndex.dropdown },
} as const;

const TRAY_TITLE_ID = 'audit-export-tray-title';

const InlineSelectDropdown = forwardRef<HTMLDivElement, SelectDropdownProps<'multi', string>>(
  function InlineSelectDropdown(props, ref) {
    const container = props.controlRef.current?.parentElement;
    const dropdown = <DefaultSelectDropdown {...props} ref={ref} />;

    return container ? createPortal(dropdown, container) : dropdown;
  },
) as SelectDropdownComponent<'multi', string>;

export function AuditExportTray({
  onClose,
  onExport,
  isExporting,
  actionTypeOptions,
  actorOptions,
  initialValues,
}: AuditExportTrayProps) {
  const { formatMessage } = useIntl();
  const trayRef = useRef<TrayRefProps>(null);

  const { value: actionTypes, onChange: onActionTypesChange } = useMultiSelect<string>({
    initialValue: initialValues?.actionTypes ?? [],
  });
  const { value: actors, onChange: onActorsChange } = useMultiSelect<string>({
    initialValue: initialValues?.actors ?? [],
  });
  const [startDate, setStartDate] = useState<Date | null>(
    initialValues?.startTime ? new Date(initialValues.startTime) : null,
  );
  const [startDateError, setStartDateError] = useState<DateInputValidationError | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(
    initialValues?.endTime ? new Date(initialValues.endTime) : null,
  );
  const [endDateError, setEndDateError] = useState<DateInputValidationError | null>(null);
  // The DatePicker is day-granular, but a "This view" preset (e.g. last 24 hours)
  // carries an exact rolling window. We hold that exact ISO as an optional
  // per-field override and send it verbatim; `null` means "no override", so the
  // matching picker date falls back to inclusive day-bounds. Editing a picker
  // clears its override.
  const [startTimeOverride, setStartTimeOverride] = useState<string | null>(
    initialValues?.startTime ?? null,
  );
  const [endTimeOverride, setEndTimeOverride] = useState<string | null>(
    initialValues?.endTime ?? null,
  );

  const title = formatMessage(messages.title);

  useEffect(() => {
    logEvent('activity_export_tray_opened', {
      componentType: ComponentType.modal,
      action: ActionType.view,
    });
  }, []);

  const closeTray = useCallback(() => {
    trayRef.current?.close();
  }, []);

  const handleCloseComplete = useCallback(() => {
    logEvent('activity_export_tray_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    closeTray();
  }, [closeTray]);

  const hasDateErrors = !!startDateError || !!endDateError;

  const handleExport = useCallback(async () => {
    logEvent('activity_export_submitted', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });

    const startTime = startTimeOverride ?? (startDate ? getExportStartTime(startDate) : '');
    const endTime = endTimeOverride ?? (endDate ? getExportEndTime(endDate) : '');

    const didExport = await onExport({ actionTypes, actors, startTime, endTime });
    if (didExport) {
      closeTray();
    }
  }, [
    onExport,
    actionTypes,
    actors,
    startDate,
    endDate,
    startTimeOverride,
    endTimeOverride,
    closeTray,
  ]);

  const handleChangeStartDate = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      setStartTimeOverride(null);
      if (date && endDate && date > endDate) {
        setEndDate(null);
        setEndDateError(null);
        setEndTimeOverride(null);
      }
    },
    [endDate],
  );

  const handleStartDateError = useCallback((error: DateInputValidationError | null) => {
    setStartDateError(error);
  }, []);

  const handleChangeEndDate = useCallback((date: Date | null) => {
    setEndDate(date);
    setEndTimeOverride(null);
  }, []);

  const handleEndDateError = useCallback((error: DateInputValidationError | null) => {
    setEndDateError(error);
  }, []);

  const maxExportDate = useMemo(() => new Date(), []);

  const highlightedDates = useMemo(() => {
    if (startDate && endDate && startDate < endDate) {
      return [[startDate, endDate]] as [Date, Date][];
    }
    if (startDate) {
      return [startDate];
    }
    return undefined;
  }, [startDate, endDate]);

  return (
    <Tray
      ref={trayRef}
      accessibilityLabel={title}
      accessibilityLabelledBy={TRAY_TITLE_ID}
      hideCloseButton
      onCloseComplete={handleCloseComplete}
      pin="right"
      preventDismiss
      styles={TRAY_STYLES}
      title={
        <HStack width="100%" alignItems="center" justifyContent="space-between">
          <Text id={TRAY_TITLE_ID} font="title3">
            {title}
          </Text>
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={handleClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
            data-dd-action-name="activity_export_close"
          />
        </HStack>
      }
      footer={
        <PageFooter
          height="auto"
          justifyContent="flex-end"
          action={
            <HStack gap={1}>
              <Button
                variant="secondary"
                transparent
                compact
                onClick={handleClose}
                data-dd-action-name="activity_export_cancel"
              >
                {formatMessage(messages.cancelButton)}
              </Button>
              <Button
                variant="primary"
                compact
                onClick={handleExport}
                loading={isExporting}
                disabled={hasDateErrors || isExporting}
                data-dd-action-name="activity_export_submit"
              >
                {formatMessage(messages.exportButton)}
              </Button>
            </HStack>
          }
        />
      }
    >
      <VStack width="100%" gap={2} paddingTop={2}>
        <Select
          type="multi"
          label={formatMessage(messages.actorLabel)}
          value={actors}
          onChange={onActorsChange}
          options={actorOptions}
          placeholder={formatMessage(messages.actorPlaceholder)}
          selectAllLabel={formatMessage(messages.actorSelectAll)}
          clearAllLabel={formatMessage(messages.actorClearAll)}
          SelectDropdownComponent={InlineSelectDropdown}
          styles={SELECT_STYLES}
          testID="audit-export-actor-select"
        />

        <Select
          type="multi"
          label={formatMessage(messages.actionTypeLabel)}
          value={actionTypes}
          onChange={onActionTypesChange}
          options={actionTypeOptions}
          placeholder={formatMessage(messages.actionTypePlaceholder)}
          selectAllLabel={formatMessage(messages.actionTypeSelectAll)}
          clearAllLabel={formatMessage(messages.actionTypeClearAll)}
          SelectDropdownComponent={InlineSelectDropdown}
          styles={SELECT_STYLES}
          testID="audit-export-action-type-select"
        />

        <HStack gap={1} width="100%">
          <DatePicker
            date={startDate}
            error={startDateError}
            maxDate={endDate ?? undefined}
            onChangeDate={handleChangeStartDate}
            onErrorDate={handleStartDateError}
            highlightedDates={highlightedDates}
            label={formatMessage(messages.startDateLabel)}
            helperText={formatMessage(messages.dateHelperText)}
            invalidDateError={formatMessage(messages.invalidDateError)}
            openCalendarAccessibilityLabel={formatMessage(messages.openStartDateCalendar)}
            closeCalendarAccessibilityLabel={formatMessage(messages.closeCalendar)}
            nextArrowAccessibilityLabel={formatMessage(messages.nextMonth)}
            previousArrowAccessibilityLabel={formatMessage(messages.previousMonth)}
            width="49%"
          />
          <DatePicker
            date={endDate}
            disabled={!startDate}
            error={endDateError}
            maxDate={maxExportDate}
            minDate={startDate ?? undefined}
            onChangeDate={handleChangeEndDate}
            onErrorDate={handleEndDateError}
            highlightedDates={highlightedDates}
            label={formatMessage(messages.endDateLabel)}
            helperText={formatMessage(messages.dateHelperText)}
            invalidDateError={formatMessage(messages.invalidDateError)}
            openCalendarAccessibilityLabel={formatMessage(messages.openEndDateCalendar)}
            closeCalendarAccessibilityLabel={formatMessage(messages.closeCalendar)}
            nextArrowAccessibilityLabel={formatMessage(messages.nextMonth)}
            previousArrowAccessibilityLabel={formatMessage(messages.previousMonth)}
            width="49%"
          />
        </HStack>
      </VStack>
    </Tray>
  );
}
